import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import api, { resolveImage } from "@/lib/api";
import Header from "@/components/Header";
import { Mail, Lock, User, Phone, ArrowRight } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [coverImage, setCoverImage] = useState("");

  useEffect(() => {
    api.get("/content").then((r) => setCoverImage(r.data?.content?.auth_register_image || "")).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error(lang === "fr" ? "Mot de passe minimum 6 caractères" : "Password min 6 characters");
      return;
    }
    setLoading(true);
    try {
      await register(form.email, form.password, form.name, form.phone);
      toast.success(lang === "fr" ? "Bienvenue dans la maison" : "Welcome to the house");
      navigate("/compte");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white text-[var(--dsf-text)] min-h-screen">
      <Header />
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen pt-20" data-testid="register-page">
        <div className="flex items-center justify-center p-8 md:p-16 bg-transparent order-2 lg:order-1">
          <form onSubmit={submit} className="w-full max-w-md">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dsf-orange)] mb-3">{t("nav.register")}</div>
            <h2 className="font-serif text-4xl text-[var(--dd-green)] mb-2">{t("auth.register.title")}</h2>
            <p className="text-[var(--dsf-text-secondary)] mb-8 text-sm">{t("auth.register.subtitle")}</p>

            <div className="space-y-5">
              <Field label={t("common.name")} icon={User} value={form.name} onChange={(v) => setForm({...form, name: v})} testid="reg-name" required />
              <Field label={t("common.email")} icon={Mail} type="email" value={form.email} onChange={(v) => setForm({...form, email: v})} testid="reg-email" required />
              <Field label={t("common.phone")} icon={Phone} value={form.phone} onChange={(v) => setForm({...form, phone: v})} testid="reg-phone" />
              <Field label={t("common.password")} icon={Lock} type="password" value={form.password} onChange={(v) => setForm({...form, password: v})} testid="reg-password" required />
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-8 disabled:opacity-50" data-testid="register-submit">
              {loading ? t("common.loading") : t("auth.register.cta")} <ArrowRight size={14} />
            </button>

            <div className="text-center text-sm text-[var(--dsf-text-secondary)] mt-6">
              {t("auth.register.has_account")}{" "}
              <Link to="/connexion" className="text-[var(--dsf-orange)] font-medium hover:underline">{t("nav.login")}</Link>
            </div>
          </form>
        </div>

        <div className="relative hidden lg:block order-1 lg:order-2 bg-gradient-to-br from-[var(--dd-burgundy,#6E0D25)] via-[var(--dd-green-deep)] to-[var(--dd-green)]">
          {coverImage && (
            <img src={resolveImage(coverImage)} alt="" className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-bl from-[var(--dd-burgundy,#6E0D25)]/75 to-[var(--dd-green)]/85" />
          <div className="relative h-full flex flex-col justify-end p-12 text-white">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dd-mustard)] mb-4">Privilèges Maison</div>
            <h1 className="font-serif text-5xl xl:text-6xl leading-[1.05] mb-6">Rejoignez la famille.</h1>
            <ul className="space-y-3 text-white/90">
              <li>· Suivi de commandes en temps réel</li>
              <li>· Accès anticipé aux nouvelles recettes</li>
              <li>· Invitations aux événements privés</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, icon: Icon, value, onChange, type = "text", testid, required }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] block mb-2 flex items-center gap-1.5">
      {Icon && <Icon size={11} />} {label}
    </label>
    <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-black/5 px-4 py-3 text-sm focus:border-[var(--dsf-green)] bg-white" data-testid={testid} />
  </div>
);
